import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import useTenantStore from '../store/tenantStore';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import PublicSiteHeader from '../components/PublicSiteHeader';
import PlatformFooter from '../components/PlatformFooter';
import api, { academicService, enrollmentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { Card, Badge, Button, EmptyState, FullPageSpinner, Modal, Field, Select, Textarea } from '../components/ui';
import { formatPrice } from '../utils/currency';
import { StarDisplay } from '../components/StarRating';
import SponsoredCoursesRow from '../components/SponsoredCoursesRow';
import TeacherByline from '../components/TeacherByline';
import { getApiErrorMessage } from '../utils/apiErrors';

const PENDING_ENROLL_KEY = 'lmsid_pending_enroll_module';
const PAGE_SIZE = 9;

function Store() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const { isAuthenticated, user } = useAuthStore();
  const featuredContentEnabled = useTenantStore((state) => state.isFeatureEnabled('featured_content'));

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [grades, setGrades] = useState([]);
  const [search, setSearch] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [hasFeaturedCourses, setHasFeaturedCourses] = useState(null);

  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [enrollForm, setEnrollForm] = useState({ grade: '', subscription: '', student_note: '', attachment: null });
  const [enrollErrors, setEnrollErrors] = useState({});
  const [enrollmentsByModule, setEnrollmentsByModule] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  useEffect(() => {
    fetchGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchModules();
    if (isAuthenticated) fetchMyEnrollments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterGrade, page, isAuthenticated]);

  // If a visitor tried to enroll before creating an account, resume that
  // intent automatically once they're logged in.
  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingId = localStorage.getItem(PENDING_ENROLL_KEY);
    if (!pendingId) return;
    localStorage.removeItem(PENDING_ENROLL_KEY);
    (async () => {
      try {
        const res = await academicService.getModule(pendingId);
        await openEnrollFor(res.data);
        addNotification(t('Welcome! Pick up where you left off and finish enrolling.'), 'success');
      } catch (e) {
        // module may no longer exist; silently drop the intent
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchMyEnrollments = async () => {
    try {
      const res = await enrollmentService.getMyEnrollments();
      const data = res.data || res;
      const list = Array.isArray(data) ? data : data.results || [];
      const map = {};
      list.forEach((e) => {
        const mid = e.module ? (typeof e.module === 'object' ? e.module.id : e.module) : null;
        if (!mid) return;
        const existing = map[mid];
        if (!existing || (e.updated_at || e.created_at || '') > (existing.updated_at || existing.created_at || '')) {
          map[mid] = e;
        }
      });
      const statusMap = {};
      Object.keys(map).forEach((k) => { statusMap[k] = String(map[k].status || '').toLowerCase(); });
      setEnrollmentsByModule(statusMap);
    } catch (err) {
      setEnrollmentsByModule({});
    }
  };

  const fetchGrades = async () => {
    try {
      const res = await academicService.getGrades();
      const data = res.data || res;
      setGrades(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setGrades([]);
    }
  };

  const fetchModules = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterGrade) params.grade = filterGrade;
      const res = await api.get('/academics/modules/', { params });
      const data = res.data.results || res.data || [];
      const modulesList = Array.isArray(data) ? data : data.results || [];
      setModules(modulesList);
      setTotalCount(res.data.count || modulesList.length);
      setTotalPages(res.data.count ? Math.ceil(res.data.count / PAGE_SIZE) : 1);
    } catch (err) {
      setModules([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const displayedModules = useMemo(() => {
    let list = [...modules];
    if (priceFilter === 'free') list = list.filter((m) => !m.price || Number(m.price) === 0);
    if (priceFilter === 'paid') list = list.filter((m) => m.price && Number(m.price) > 0);
    if (sortBy === 'price_asc') list.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    else if (sortBy === 'price_desc') list.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    else if (sortBy === 'newest') list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return list;
  }, [modules, priceFilter, sortBy]);

  const loadModuleDetails = async (module) => {
    if (module.categories && module.effective_payment_accounts) return module;
    const response = await academicService.getModule(module.id);
    return response.data;
  };

  const openModuleView = async (module) => {
    setSelectedModule(module);
    setShowViewModal(true);
    try {
      setSelectedModule(await loadModuleDetails(module));
    } catch (err) {
      setShowViewModal(false);
      addNotification(t('Failed to load course details.'), 'error');
    }
  };

  const openEnrollFor = async (module) => {
    let fullModule;
    try {
      fullModule = await loadModuleDetails(module);
    } catch (err) {
      addNotification(t('Failed to load course details.'), 'error');
      return;
    }
    setSelectedModule(fullModule);
    setEnrollForm((prev) => ({
      ...prev,
      grade: fullModule.grades && fullModule.grades.length > 0 ? fullModule.grades[0].id : '',
      student_note: '',
      attachment: null,
      subscription: '',
    }));
    try {
      const res = await enrollmentService.getSubscriptions({ module: fullModule.id });
      const data = res.data.results || res.data || [];
      const list = Array.isArray(data) ? data : data.results || [];
      setSubscriptions(list);
      if (list.length > 0) setEnrollForm((prev) => ({ ...prev, subscription: list[0].id }));
    } catch (err) {
      setSubscriptions([]);
    }
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
    setEnrollErrors({});
    setShowEnrollModal(true);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim() || !selectedModule || !enrollForm.subscription) return;
    setApplyingCoupon(true);
    setCouponError('');
    try {
      const res = await enrollmentService.validateCoupon(couponCode.trim(), selectedModule.id, enrollForm.subscription);
      setCouponResult(res.data);
    } catch (err) {
      setCouponResult(null);
      setCouponError(getApiErrorMessage(err, t, 'This coupon code is invalid, expired, or does not apply to this course.'));
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleEnrollClick = (module) => {
    if (!isAuthenticated) {
      localStorage.setItem(PENDING_ENROLL_KEY, module.id);
      addNotification(t('Create a free account to enroll — we will bring you right back here.'), 'info');
      navigate('/register');
      return;
    }
    if (!user?.roles?.some((r) => r.name.toLowerCase() === 'student')) {
      addNotification(t('Only student accounts can enroll in courses.'), 'error');
      return;
    }
    openEnrollFor(module);
  };

  const handlePreviewClick = (module) => {
    const target = `/modules/${module.id}?preview=1`;
    if (!isAuthenticated) {
      addNotification(t('Log in to preview free course lessons.'), 'info');
      navigate('/login', { state: { from: target } });
      return;
    }
    navigate(target);
  };

  const handleEnrollChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachment') {
      const file = files[0];
      setEnrollForm((prev) => ({ ...prev, attachment: file }));
      setEnrollErrors((prev) => ({ ...prev, attachment: validateAttachment(file) }));
    } else {
      setEnrollForm((prev) => ({ ...prev, [name]: value }));
      // A previously-applied coupon's discount was computed against the plan
      // selected at the time — switching plans makes that number wrong, so
      // require it to be re-applied rather than silently showing a stale figure.
      if (name === 'subscription') {
        setCouponResult(null);
        setCouponError('');
      }
    }
  };

  const validateAttachment = (file) => {
    if (!file) return '';
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) return t('Invalid file type. Allowed: pdf, jpg, jpeg, png.');
    if (file.size > 5 * 1024 * 1024) return t('File too large. Max size is 5MB.');
    return '';
  };

  const submitEnrollment = async (e) => {
    e && e.preventDefault();
    const attachErr = validateAttachment(enrollForm.attachment);
    if (attachErr) {
      setEnrollErrors((prev) => ({ ...prev, attachment: attachErr }));
      addNotification(attachErr, 'error');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('module', selectedModule.id);
      if (enrollForm.grade) fd.append('grade', enrollForm.grade);
      if (enrollForm.subscription) fd.append('subscription', enrollForm.subscription);
      if (enrollForm.student_note) fd.append('student_note', enrollForm.student_note);
      if (enrollForm.attachment) fd.append('attachment', enrollForm.attachment);
      if (couponResult?.valid) fd.append('coupon_code', couponCode.trim());

      const res = await api.post('/enrollments/enrollments/', fd);
      setShowEnrollModal(false);
      fetchMyEnrollments();
      if (res.data?.status === 'active') {
        addNotification(t("You're enrolled! This course doesn't require approval — dive right in."), 'success');
        navigate(`/modules/${selectedModule.id}`);
      } else {
        addNotification(t('Enrollment request sent. Awaiting approval.'), 'success');
      }
    } catch (err) {
      addNotification(getApiErrorMessage(err, t, 'Failed to submit enrollment'), 'error');
    }
  };

  const renderActionButton = (m) => {
    const preview = <Button size="sm" variant="secondary" onClick={() => handlePreviewClick(m)}>{t('Preview')}</Button>;
    const actions = (primary) => <div className="flex items-center justify-end gap-2">{preview}{primary}</div>;
    if (!isAuthenticated) {
      return actions(<Button size="sm" onClick={() => handleEnrollClick(m)}>{t('Enroll')}</Button>);
    }
    const status = enrollmentsByModule[m.id];
    if (status === 'active') {
      return actions(<Button size="sm" onClick={() => navigate(`/modules/${m.id}`)}>{t('Access Course')}</Button>);
    }
    if (status === 'pending') {
      return actions(<Button size="sm" variant="secondary" onClick={() => navigate('/enrollments')}>{t('Pending')}</Button>);
    }
    return actions(<Button size="sm" onClick={() => handleEnrollClick(m)}>{t('Enroll')}</Button>);
  };

  const featuredCoursesSection = featuredContentEnabled ? (
    <section className={`${hasFeaturedCourses === false ? 'hidden' : ''} relative overflow-hidden border-b border-primary-100 bg-gradient-to-br from-primary-50 via-white to-cyan-50 py-16 sm:py-20`}>
      <div className="absolute -right-32 -top-24 h-80 w-80 rounded-full bg-primary-200/35 blur-3xl" aria-hidden="true" />
      <div className="absolute -left-36 bottom-0 h-72 w-72 rounded-full bg-accent-200/25 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6">
        <SponsoredCoursesRow
          eyebrow={t('Top learning picks')}
          title={t('Featured Courses')}
          subtitle={t('Handpicked courses worth a look, right now.')}
          renderAction={(m) => renderActionButton(m)}
          onView={openModuleView}
          onAvailabilityChange={setHasFeaturedCourses}
          autoAdvance
          centered
        />
      </div>
    </section>
  ) : null;

  const storeContent = (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold tracking-tight text-gray-900">{t('Course Store')}</h1>
        <p className="mt-2 text-gray-600">
          {totalCount > 0
            ? t('{{count}} courses available', { count: totalCount })
            : t('Browse available courses')}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-surface-200 shadow-card p-4 mb-8 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search courses...')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500"
          />
        </div>
        <select value={filterGrade} onChange={(e) => { setFilterGrade(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
          <option value="">{t('All grades')}</option>
          {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
          <option value="all">{t('All prices')}</option>
          <option value="free">{t('Free')}</option>
          <option value="paid">{t('Paid')}</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
          <option value="name">{t('Sort: Name (A-Z)')}</option>
          <option value="price_asc">{t('Sort: Price (Low to High)')}</option>
          <option value="price_desc">{t('Sort: Price (High to Low)')}</option>
          <option value="newest">{t('Sort: Newest')}</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : displayedModules.length === 0 ? (
        <Card>
          <EmptyState title={t('No courses match your filters')} description={t('Try a different search term or clear the filters.')} />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedModules.map((m) => (
              <Card key={m.id} padded={false} className="flex flex-col overflow-hidden hover:shadow-card-hover transition">
                <div
                  className="h-40 bg-gray-100 bg-center bg-cover"
                  style={m.thumbnail ? { backgroundImage: `url(${m.thumbnail})` } : {}}
                >
                  {!m.thumbnail && (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
                      <svg className="w-10 h-10 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Badge tone={m.price && Number(m.price) > 0 ? 'blue' : 'green'}>
                      {m.price && Number(m.price) > 0 ? formatPrice(m.price, m.currency) : t('Free')}
                    </Badge>
                    {m.grades && m.grades.length > 0 && (
                      <span className="text-xs text-gray-500 truncate">{m.grades[0].name}</span>
                    )}
                  </div>
                  <h3 className="font-display font-bold text-gray-900 mb-1.5">{m.name}</h3>
                  <TeacherByline teachers={m.teachers} className="mb-1.5" />
                  {m.average_rating != null && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <StarDisplay value={m.average_rating} size="w-3.5 h-3.5" />
                      <span className="text-xs text-gray-500">
                        {Number(m.average_rating).toFixed(1)} ({m.ratings_count})
                      </span>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 line-clamp-2 flex-1">{m.description}</p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <button
                      onClick={() => openModuleView(m)}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {t('View')}
                    </button>
                    {renderActionButton(m)}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-8 gap-3">
              <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>{t('Previous')}</Button>
              <span className="text-sm text-gray-600">{t('Page')} {page} / {totalPages}</span>
              <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>{t('Next')}</Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  const modals = (
    <>
      <Modal open={showEnrollModal && !!selectedModule} onClose={() => setShowEnrollModal(false)} title={t('Request Enrollment')} size="lg">
        {selectedModule && (
          <form onSubmit={submitEnrollment} className="space-y-4">
            {selectedModule.requires_approval === false && (
              <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-800 flex items-center gap-2">
                <span>✅</span>
                <span>{t('This course does not require approval — you get instant access as soon as you enroll.')}</span>
              </div>
            )}

            {selectedModule.enrollment_directives && (
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800">
                <strong>{t('Enrollment directives:')}</strong>
                <div className="mt-1 whitespace-pre-line text-gray-700">{selectedModule.enrollment_directives}</div>
              </div>
            )}

            {selectedModule.effective_payment_accounts && selectedModule.effective_payment_accounts.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">{t('Where to send payment')}</p>
                {selectedModule.effective_payment_accounts.map((acc) => (
                  <div key={acc.id} className="p-3 bg-primary-50 border border-primary-100 rounded-lg text-sm text-gray-700">
                    <p className="font-semibold text-primary-800">
                      {{ bank_transfer: '🏦', mobile_payment: '📱', cash: '💵', other: '💳' }[acc.method_type] || '💳'} {acc.label}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {acc.account_holder_name && <p>{t('Holder')}: {acc.account_holder_name}</p>}
                      {acc.account_number && <p>{t('Account #')}: {acc.account_number}</p>}
                      {acc.bank_name && <p>{t('Bank')}: {acc.bank_name}</p>}
                      {acc.instructions && <p className="italic text-gray-600 mt-1">{acc.instructions}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Field label={t('Grade')} required>
              <Select name="grade" value={enrollForm.grade} onChange={handleEnrollChange} required>
                {selectedModule.grades && selectedModule.grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            </Field>
            <Field label={t('Subscription')} required>
              <Select name="subscription" value={enrollForm.subscription} onChange={handleEnrollChange} required>
                <option value="">{t('Select a plan')}</option>
                {subscriptions.map((s) => <option key={s.id} value={s.id}>{s.name} — ${s.price}</option>)}
              </Select>
            </Field>

            <Field label={t('Coupon Code (optional)')}>
              <div className="flex gap-2">
                <input
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponResult(null); setCouponError(''); }}
                  placeholder="WELCOME10"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <Button type="button" variant="secondary" size="sm" loading={applyingCoupon} onClick={applyCoupon} disabled={!couponCode.trim()}>
                  {t('Apply')}
                </Button>
              </div>
              {couponResult?.valid && (
                <p className="mt-1.5 text-sm text-emerald-700">
                  ✅ {t('Coupon applied — discounted price')}: {formatPrice(couponResult.discounted_price, selectedModule.currency)}
                </p>
              )}
              {couponError && <p className="mt-1.5 text-sm text-red-600">{couponError}</p>}
            </Field>

            <Field label={t('Note (optional)')}>
              <Textarea name="student_note" value={enrollForm.student_note} onChange={handleEnrollChange} rows={3} placeholder={t('Any notes for the teacher or admin')} />
            </Field>
            <Field label={t('Attachment (proof) — optional')} error={enrollErrors.attachment} hint={t('Upload payment proof or other supporting document (jpg, png, pdf)')}>
              <input type="file" name="attachment" accept="image/jpeg,image/png,application/pdf" onChange={handleEnrollChange} className="text-sm" />
            </Field>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowEnrollModal(false)}>{t('Cancel')}</Button>
              <Button type="submit" className="flex-1" disabled={!!enrollErrors.attachment}>
                {selectedModule.requires_approval === false ? t('Enroll Now') : t('Submit Request')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={showViewModal && !!selectedModule} onClose={() => setShowViewModal(false)} title={selectedModule?.name} size="lg">
        {selectedModule && (
          <div className="space-y-4">
            {selectedModule.intro_video && (
              selectedModule.intro_video.includes('youtube') || selectedModule.intro_video.includes('youtu.be') ? (
                <iframe
                  title="Intro video"
                  src={selectedModule.intro_video.includes('embed') ? selectedModule.intro_video : selectedModule.intro_video.includes('watch') ? selectedModule.intro_video.replace('watch?v=', 'embed/') : selectedModule.intro_video.replace('youtu.be/', 'www.youtube.com/embed/')}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full aspect-video rounded-lg"
                />
              ) : (
                <video src={selectedModule.intro_video} controls className="w-full rounded-lg" />
              )
            )}
            <p className="text-sm text-gray-600">{selectedModule.description}</p>
            <div className="max-h-64 overflow-y-auto space-y-4">
              {selectedModule.categories && selectedModule.categories.length > 0 ? (
                selectedModule.categories.map((cat) => (
                  <div key={cat.id}>
                    <h4 className="font-semibold text-gray-800 text-sm">{cat.name}</h4>
                    <ul className="mt-1.5 space-y-1">
                      {cat.lessons && cat.lessons.map((ls) => (
                        <li key={ls.id} className="text-sm text-gray-600 flex items-center gap-2">
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          {ls.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">{t('No categories available')}</p>
              )}
            </div>
            <div className="flex justify-end pt-2 gap-3">
              <Button variant="secondary" onClick={() => setShowViewModal(false)}>{t('Close')}</Button>
              <Button variant="secondary" onClick={() => { setShowViewModal(false); handlePreviewClick(selectedModule); }}>{t('Preview')}</Button>
              <Button onClick={() => { setShowViewModal(false); handleEnrollClick(selectedModule); }}>{t('Enroll')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );

  if (!isAuthenticated) {
    return (
      <div className="h-screen overflow-y-auto bg-surface-50 text-gray-900">
        <PublicSiteHeader active="store" />
        {featuredCoursesSection}
        {storeContent}
        <PlatformFooter />
        {modals}
      </div>
    );
  }

  if (!user) return <FullPageSpinner />;

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Store')} subtitle={t('Browse available courses')} />
        <main className="flex-1 overflow-y-auto">
          {featuredCoursesSection}
          {storeContent}
        </main>
      </div>
      {modals}
    </div>
  );
}

export default Store;
